import "./styles.css";

import PartySocket from "partysocket";
import {
  WORLD_WIDTH, WORLD_HEIGHT, GROUND_THICKNESS, PLAYER_RADIUS, PLATFORM_THICKNESS,
  MOVE_SPEED, TICK_MS, SUB_STEPS,
  type Point, type Keys,
} from "./physics";

declare const PARTYKIT_HOST: string;

// Everything that describes the *shared simulation* — world dimensions,
// player/platform geometry, movement speed, tick rate, and the Point/Keys
// shapes — is imported from physics.ts rather than hand-mirrored. This client
// used to keep its own copies of these (a stale comment here claimed "this is
// a separate bundle that can't import the backend module" — true of
// directory.ts, which pulls in partykit/server, but physics.ts is a pure
// shared module with zero server-runtime imports and is erased to nothing at
// compile time when only its types/constants are used — see its header
// comment for the empirical bundle-size proof). Faithful local prediction
// (predictLocalX below) requires the *exact* model the server runs; a
// hand-mirrored copy would only guarantee drift. See CLAUDE.md's note on this
// single-sourcing convention change.
const CHEESE_RADIUS = 14; // px — mirrors the server's CHEESE_RADIUS; purely cosmetic here, since the cheese is a server-authoritative target point with no physics body for this client to derive geometry from
const MIN_POINT_DISTANCE = 4; // px — minimum spacing between captured drawing points, matches the prototype
const RDP_EPSILON = 2; // px — Ramer-Douglas-Peucker tolerance; the bandwidth optimization the prototype deferred (see CLAUDE.md)
const ERASE_HIT_TOLERANCE = PLATFORM_THICKNESS / 2 + 6; // px — how close a right-click must land to a platform's stroke to erase it; a little forgiveness beyond the visual half-thickness so thin/precise strokes stay easy to target
const ERASE_PREVIEW_COLOR = "#ff6b6b"; // a "danger" red, deliberately outside PALETTE_COLORS' pastel set (and distinct from the "(you)" green) — communicates "about to be removed," not "an avatar/platform color choice"
const MAX_CHAT_LENGTH = 240; // mirrors the server's MAX_CHAT_LENGTH — caps the <input> so nothing gets typed that the server would just truncate anyway

// How far in the past to render, for entity interpolation. Two snapshot
// intervals: one is the minimum (so there's normally a newer snapshot to tween
// toward), the second is jitter/late-packet margin (one delayed or dropped
// snapshot still leaves a bracket to interpolate within). Larger = smoother
// under bad networks but more input latency on the local avatar.
const INTERP_DELAY_MS = TICK_MS * 2;

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
canvas.width = WORLD_WIDTH;
canvas.height = WORLD_HEIGHT;
const ctx = canvas.getContext("2d")!;

// The world has a fixed resolution (it's the server's coordinate space); scale
// the canvas element to fit whatever viewport it's shown in via CSS, keeping
// its internal drawing-buffer size — and therefore the server's coordinates —
// untouched.
function fitCanvasToViewport() {
  const scale = Math.min(window.innerWidth / WORLD_WIDTH, window.innerHeight / WORLD_HEIGHT);
  // Round to whole CSS pixels — a fixed-resolution canvas stretched to a
  // sub-pixel size gets blurred by the browser's scaling/anti-aliasing.
  canvas.style.width = `${Math.round(WORLD_WIDTH * scale)}px`;
  canvas.style.height = `${Math.round(WORLD_HEIGHT * scale)}px`;
}
window.addEventListener("resize", fitCanvasToViewport);
fitCanvasToViewport();

// Color palette — a small preset swatch picker (the GDD's draw message format
// always includes a color; a full custom picker is more UI than this whiteboard
// needs right now). Built in JS rather than index.html to keep the feature
// self-contained in one place, matching how keyboard capture lives here too.
// ROYGBIV-ordered (Indigo dropped — at pastel saturation/lightness it reads as
// indistinguishable from Blue/Violet, a well-known critique of Newton's 7-way
// split; six evenly-spaced hues give better at-a-glance separation than seven
// crowded ones). Keeps Yellow/Green/Blue/Violet from the old palette — they
// were never the problem — and replaces only the two near-identical "reds"
// (#f6a5c0 a pink, #f3a3a3 a salmon — easy to confuse at a glance) with one
// clear Red plus a new Orange to complete the spectrum. Hues land roughly at
// 5°/27°/41°/154°/219°/262°, each ~15-65° from its neighbors — enough gap to
// stay distinct while keeping the same soft pastel character throughout.
const PALETTE_COLORS = ["#f4978e", "#f7a663", "#ffd479", "#8fd3b6", "#a3c4f3", "#c5a3f3"];
let selectedColor = PALETTE_COLORS[0];

const palette = document.createElement("div");
palette.id = "palette";
for (const color of PALETTE_COLORS) {
  const swatch = document.createElement("button");
  swatch.classList.add("swatch");
  swatch.style.backgroundColor = color;
  swatch.setAttribute("aria-label", `Draw in ${color}`);
  swatch.classList.toggle("selected", color === selectedColor);
  swatch.addEventListener("click", () => {
    selectedColor = color;
    for (const el of palette.querySelectorAll(".swatch")) el.classList.remove("selected");
    swatch.classList.add("selected");
  });
  palette.appendChild(swatch);
}
document.body.appendChild(palette);

type PlayerSnapshot = { id: string; x: number; y: number; vx: number; vy: number };
type Platform = { id: string; points: Point[]; color: string; owner: string };
type Identity = { username: string; color: string };

// A line in the chat log — either a real chat message (rendered as a colored
// username + text, mirroring how avatars show identity) or a system note for
// join/leave events (rendered muted/italic, like .join-hint). System entries
// are derived client-side from player_joined/player_left — see the chat setup
// in startGame for why that's preferable to a dedicated protocol message.
type ChatEntry =
  | { kind: "chat"; username: string; color: string; text: string }
  | { kind: "system"; text: string };

// Wire shape for world_state's replayed join/leave/chat history (mirrors
// server.ts's ActivityEntry/activityLog). Join/leave arrive as structured
// {kind, username} rather than pre-formatted text — joinLeaveText below turns
// them into the same "<username> joined"/"<username> left" wording the live
// player_joined/player_left cases already produce, so replayed and live system
// entries read identically with the format defined in exactly one place.
type ActivityEntry =
  | { kind: "join" | "leave"; username: string }
  | { kind: "chat"; username: string; color: string; text: string };

function joinLeaveText(kind: "join" | "leave", username: string): string {
  return kind === "join" ? `${username} joined` : `${username} left`;
}

const DEFAULT_AVATAR_COLOR = "#f4c95d"; // matches the server's DEFAULT_AVATAR_COLOR fallback

type Snapshot = { receivedAt: number; players: PlayerSnapshot[] };

// Recent position snapshots, oldest-first, for entity interpolation. The render
// loop (frame -> playersAt) draws a blend of the two snapshots bracketing
// "now - INTERP_DELAY_MS" rather than the newest one outright, so 20Hz server
// updates render smoothly at display rate. Pruned to just the window the current
// render needs (see the snapshot case below).
let snapshotBuffer: Snapshot[] = [];

// Identities (username + chosen avatar color) arrive separately from position
// snapshots (world_state on join, player_joined/player_left as the roster
// changes) — keyed by connection id so they can be looked up while rendering
// each avatar from the interpolated snapshot buffer.
const roster = new Map<string, Identity>();

// Platforms arrive via world_state (on join) and platform_added (live draws),
// keyed by id — the server is the only source of truth for what's "real";
// rendering happens purely by regenerating geometry from each one's point array.
const platforms = new Map<string, Platform>();

// Minigame display state — populated *only* from server broadcasts, never
// computed locally, mirroring snapshotBuffer/platforms's "server is sole
// source of truth" posture. null means "nothing to show"; renderMinigameBanner
// (built in startGame, alongside the rest of this connection's UI) is the one
// place that turns these into what the player sees.
let voteStatus: { proposedByUsername: string; yesCount: number; totalCount: number } | null = null;
let raceStatus: { cheese: Point } | null = null;
// The "setup latency" gap between a yes-vote and the race itself (see
// minigame_countdown below and CLAUDE.md/the cheese-race plan's note on why
// minigame_vote_resolved and minigame_started are kept as separate broadcasts).
// `endsAt` is the server's absolute timestamp — frame() recomputes the seconds
// remaining every frame from it (Date.now() drift-free, the same reasoning
// behind the server modeling it as startedAt/endsAt rather than a duration).
let countdownStatus: { endsAt: number } | null = null;

// Right-click hit-testing: finds whichever *segment*, across every platform,
// passes closest to `point` within ERASE_HIT_TOLERANCE — checking each
// consecutive point pair the same way drawPlatformPath renders them, so "what
// you can click" always matches "what you see". Picking by closest distance
// (rather than e.g. most-recently-drawn / topmost) is deliberate: Map
// iteration order reflects each client's own arrival order for these platforms
// — via world_state's storage listing for pre-existing ones, live broadcasts
// for new ones — which can differ between clients, so there's no globally-
// consistent "z-order" to pick by. Closest-to-cursor is both simpler and more
// intuitive to aim. Returns the segment's *index within its platform* (not the
// segment's geometry) — that's what the server needs to cut the right piece
// out of the right platform's point array (see splitPoints server-side).
function findSegmentAt(point: Point): { platformId: string; segmentIndex: number } | null {
  let closest: { platformId: string; segmentIndex: number } | null = null;
  let closestDist = ERASE_HIT_TOLERANCE;
  for (const platform of platforms.values()) {
    for (let i = 0; i < platform.points.length - 1; i++) {
      const dist = pointToSegmentDistance(point, platform.points[i], platform.points[i + 1]);
      if (dist < closestDist) {
        closestDist = dist;
        closest = { platformId: platform.id, segmentIndex: i };
      }
    }
  }
  return closest;
}

// Narrows an arbitrary JSON-decoded value down to "a non-null object whose
// string-keyed properties we can safely probe" — the minimal common shape
// every server message must have before we can even look at its `type`.
function asRecord(data: unknown): Record<string, unknown> | null {
  return typeof data === "object" && data !== null ? (data as Record<string, unknown>) : null;
}

// Shape-check vocabulary for inbound server messages — see CLAUDE.md's
// "Client-side validation posture toward the server" convention. The server
// is the sole source of truth and already deep-validates every message's
// fields before it ever broadcasts (gdd_partykit.md's "Schema Validation"/
// "Numbers Only" sections), so the client's job is only to confirm a message
// has the *shape* its declared type promises — right top-level keys, roughly-
// right primitive types — not to re-derive the server's own per-field
// validation a second time (two independent copies of "what does a valid
// Platform look like" will only drift apart over time). Anything that claims
// a known `type` but fails even this minimal check isn't "untrusted input"
// (filtering that is the server's job): it's a bug worth knowing about — a
// stale client, a version-skewed deploy, a protocol typo — so it's logged
// rather than silently dropped.
const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function warnUnexpectedShape(type: string, msg: Record<string, unknown>) {
  console.warn(`Ignoring "${type}" message from server — unexpected shape:`, msg);
}

function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function perpendicularDistance(point: Point, lineStart: Point, lineEnd: Point): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  if (dx === 0 && dy === 0) return distance(point, lineStart);
  const t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (dx * dx + dy * dy);
  return distance(point, { x: lineStart.x + t * dx, y: lineStart.y + t * dy });
}

// Distance from a point to a finite *segment* (unlike perpendicularDistance,
// which measures to the infinite line through the endpoints) — `t` is clamped
// to [0, 1] so the nearest point can't fall beyond either endpoint. This is
// what makes right-click hit-testing feel right: clicking just past the tip
// of a stroke shouldn't still register as "on" its underlying line.
function pointToSegmentDistance(point: Point, segStart: Point, segEnd: Point): number {
  const dx = segEnd.x - segStart.x;
  const dy = segEnd.y - segStart.y;
  if (dx === 0 && dy === 0) return distance(point, segStart);
  const t = Math.max(0, Math.min(1, ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / (dx * dx + dy * dy)));
  return distance(point, { x: segStart.x + t * dx, y: segStart.y + t * dy });
}

// Ramer-Douglas-Peucker: recursively discards points that fall within `epsilon`
// of the straight line between the current segment's endpoints, keeping only
// those that meaningfully change the path's shape. This is the network-bandwidth
// optimization the prototype deliberately deferred (see CLAUDE.md's conventions) —
// it matters now because every point gets transmitted and stored.
function rdpSimplify(points: Point[], epsilon: number): Point[] {
  if (points.length < 3) return points;

  const first = points[0];
  const last = points[points.length - 1];
  let maxDist = 0;
  let maxIndex = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], first, last);
    if (dist > maxDist) {
      maxDist = dist;
      maxIndex = i;
    }
  }

  if (maxDist > epsilon) {
    const left = rdpSimplify(points.slice(0, maxIndex + 1), epsilon);
    const right = rdpSimplify(points.slice(maxIndex), epsilon);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
}

// The world has a fixed resolution but the canvas element is CSS-scaled to fit
// the viewport (see fitCanvasToViewport), so client coordinates must be
// converted back into world/buffer space for both drawing capture and physics.
function canvasPoint(event: MouseEvent): Point {
  const rect = canvas.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) * canvas.width) / rect.width,
    y: ((event.clientY - rect.top) * canvas.height) / rect.height,
  };
}

// In-progress freehand stroke — module-level so the render loop's
// drawInProgressPath can preview it regardless of where capture is wired up.
let drawing: Point[] | null = null;

// Right-click-drag erase capture: a platformId → segmentIndex-set accumulator,
// alive only for the duration of one drag gesture (null between gestures,
// mirroring `drawing`'s null-when-idle convention). Every segment the cursor
// crosses gets recorded — Sets dedupe a drag doubling back over itself — and
// on release the whole accumulated batch goes out as ONE `erase` message,
// exactly how `drawing` collects a whole stroke before sending one `draw`.
let erasing: Map<string, Set<number>> | null = null;

let conn: PartySocket;

// Held-key state — module-level (hoisted out of startGame) so the module-level
// frame()/predictLocalX can read it every frame; setKey (wired up inside
// startGame, where the listeners and `chatOpen` it depends on live) is what
// mutates it and sends `input` messages on change. Same "free function reads
// shared module state" shape as countdownEl/conn below.
const keys: Keys = { left: false, right: false, jump: false };

// Local-player prediction state (Phase 1 — horizontal-only; see CLAUDE.md's
// note on this being a deliberate, scoped exception to "the client does no
// local physics or prediction," and prediction_plan.md for the staged design).
// `localX` is null until the first snapshot seeds it — there's nothing to
// predict from before we know where the server actually put us.
let localX: number | null = null;
let lastServerVx = 0; // the local player's vx from the most recent snapshot — 0 after a wall collision (Matter.js zeroes it), ±MOVE_SPEED during free movement; used to detect blocked state and suppress prediction
let lastKeyChangeTime = -Infinity; // timestamp of the last horizontal key state change — used for a grace period so wall-collision detection doesn't suppress prediction during the ~1-tick window before the server has processed a newly-pressed key
const RECONCILE_BLEND = 0.15; // per-snapshot fraction to ease predicted x toward authority — small enough that corrections read as a gentle pull, not a snap
const PREDICT_SNAP_PX = 64; // error past this = hard snap rather than smoothing through it (a desync this big means something other than network jitter — easing through it would just look like floating)

// frame()'s own per-frame delta, for predictLocalX's dtMs. Reset right before
// the render loop starts (see the bottom of startGame) so a long join-screen
// pause before the first frame can't register as one enormous dtMs.
let lastFrameTime = performance.now();

// The large centered countdown readout — module-level (like `conn`) because
// frame() (a free function, per this file's "render loop drives every visual
// based on current state" shape) needs to update it every frame from
// countdownStatus/Date.now(), but the element itself is built in startGame
// alongside the rest of this connection's UI (see updateCountdownDisplay).
let countdownEl: HTMLDivElement;

// Predicts the local avatar's x immediately from its own held keys — removing
// the ~INTERP_DELAY_MS lag for the dominant felt latency (running). y stays
// interpolated (see frame()): jumps/falls still lag slightly behind input,
// an accepted Phase 1 tradeoff (see prediction_plan.md). Mirrors the server's
// direct-velocity horizontal model — applyMovement in physics-bodies.ts sets
// `vx = ±MOVE_SPEED` once per tick, then the engine runs SUB_STEPS sub-steps
// at SUB_STEP_MS each, so the actual displacement per tick is
// MOVE_SPEED * SUB_STEPS. Integrating that at the per-frame rate
// (MOVE_SPEED * SUB_STEPS * dtMs/TICK_MS) and clamping to the world's side
// walls is a faithful, Matter-free rebuild of "where would the server be by
// now" for pure horizontal motion.
function predictLocalX(dtMs: number) {
  if (localX === null) return;
  let dir = 0;
  if (keys.left) dir -= 1;
  if (keys.right) dir += 1;
  if (dir === 0) return;
  // Grace period: skip the wall-detection check for TICK_MS*2 after any
  // horizontal key change. The server needs up to one full tick to process the
  // new input and reply with a non-zero vx; applying the check before that
  // snapshot arrives would suppress prediction for the first ~50ms of every
  // key press (lastServerVx is 0 while the player was at rest), making the
  // avatar feel unresponsive right at the moment the key is pressed.
  const inGrace = performance.now() - lastKeyChangeTime < TICK_MS * 2;
  if (!inGrace) {
    // If the server's last reported vx doesn't agree with the held direction,
    // the player is blocked — Matter.js zeroes vx after a collision with a
    // static body (restitution=0) while applyMovement keeps requesting
    // ±MOVE_SPEED. Don't predict past what the server says is reachable.
    const sameDir = dir > 0 ? lastServerVx > 0 : lastServerVx < 0;
    if (!sameDir) return;
  }
  localX += dir * MOVE_SPEED * SUB_STEPS * (dtMs / TICK_MS);
  localX = Math.max(PLAYER_RADIUS, Math.min(WORLD_WIDTH - PLAYER_RADIUS, localX));
}

// Opens the room connection and wires up everything that depends on it —
// called once the join screen has collected the player's identity. Keeping
// this deferred (rather than connecting at module load) is what lets the
// lobby pick a room and identity *before* the world starts simulating for us.
function startGame(roomId: string, identity: Identity) {
  conn = new PartySocket({
    host: PARTYKIT_HOST,
    room: roomId,
    query: { username: identity.username, color: identity.color },
  });

  // Keyboard capture — sends an `input` message only when the {left,right,jump}
  // state actually changes, rather than on every keydown/keyup repeat. `keys`
  // itself lives at module scope (see its declaration) so frame()/predictLocalX
  // can read it every frame for local-player prediction; remote players still
  // come purely from server snapshots — only the local avatar's x is predicted.
  function setKey(code: string, value: boolean) {
    if (chatOpen) return; // the chat input owns the keyboard while it's open — see the chat setup further down
    let changed = false;
    switch (code) {
      case "ArrowLeft":
      case "KeyA":
        changed = keys.left !== value;
        keys.left = value;
        if (changed) lastKeyChangeTime = performance.now();
        break;
      case "ArrowRight":
      case "KeyD":
        changed = keys.right !== value;
        keys.right = value;
        if (changed) lastKeyChangeTime = performance.now();
        break;
      case "ArrowUp":
      case "KeyW":
      case "Space":
        changed = keys.jump !== value;
        keys.jump = value;
        break;
    }
    if (changed) conn.send(JSON.stringify({ type: "input", keys }));
  }

  window.addEventListener("keydown", (event) => setKey(event.code, true));
  window.addEventListener("keyup", (event) => setKey(event.code, false));

  // Drawing capture — mousedown/mousemove/mouseup into a raw {x,y}[] path,
  // mirroring the prototype, then RDP-simplified and sent as a `draw` message
  // on stroke completion.
  canvas.addEventListener("mousedown", (event) => {
    if (event.button === 2) {
      // Right button starts an erase drag — see `erasing`'s declaration and
      // the contextmenu listener below (which only suppresses the native menu;
      // the actual hit-testing lives here and in mousemove/mouseup, exactly
      // mirroring how drawing capture spans mousedown/mousemove/mouseup).
      erasing = new Map();
      recordEraseHit(canvasPoint(event));
      return;
    }
    if (event.button !== 0) return; // only the primary (left) button draws
    drawing = [canvasPoint(event)];
  });

  canvas.addEventListener("mousemove", (event) => {
    if (erasing) recordEraseHit(canvasPoint(event));
    if (!drawing) return;
    const p = canvasPoint(event);
    if (distance(drawing[drawing.length - 1], p) >= MIN_POINT_DISTANCE) {
      drawing.push(p);
      // Cheese Race caps every stroke at exactly one segment (see the server's
      // matching handleDraw check, and CLAUDE.md). The instant RDP would keep a
      // third point — i.e. a corner just appeared, "a second segment wants to
      // start" — cut the stroke short *right then*, not after release: waiting
      // for mouseup to truncate would feel like a bait-and-switch (draw a long
      // curve, release, watch it snap to a stub).
      if (raceStatus && rdpSimplify(drawing, RDP_EPSILON).length > 2) finalizeRaceStroke();
    }
  });

  function finishDrawing() {
    if (!drawing) return;
    const path = drawing;
    drawing = null;
    if (path.length < 2) return;
    const simplified = rdpSimplify(path, RDP_EPSILON);
    if (simplified.length >= 2) {
      conn.send(JSON.stringify({ type: "draw", points: simplified, color: selectedColor }));
    }
  }

  // Sends the in-progress stroke as just its first segment, the moment a
  // second one would have started. Sending only the first two simplified
  // points is what "exactly one segment" means — mirrors finishDrawing's
  // "null `drawing` first, then act on the captured path" ordering, so
  // finishDrawing's existing `if (!drawing) return` already makes it a safe
  // no-op on the eventual mouseup (no new "locked" state needed: drawing===null
  // already means "not capturing," and mousedown unconditionally re-arms it).
  function finalizeRaceStroke() {
    if (!drawing) return;
    const simplified = rdpSimplify(drawing, RDP_EPSILON);
    drawing = null;
    if (simplified.length >= 2) {
      conn.send(JSON.stringify({ type: "draw", points: simplified.slice(0, 2), color: selectedColor }));
    }
  }

  // Records one hit-tested point into the in-progress erase-drag accumulator —
  // shared by the initial mousedown (so a plain right-click-without-dragging
  // still erases something) and every subsequent mousemove while dragging.
  function recordEraseHit(point: Point) {
    if (!erasing) return;
    // Cheese Race caps a whole drag-gesture's worth of erasing to its first
    // hit only (mirrors the server's matching handleErase truncation —
    // CLAUDE.md). `erasing.size > 0` is the natural one-line proxy for "have
    // we already recorded a hit this gesture," exactly aligned with the
    // server's "first hit only" — no separate counter to keep in sync. The
    // real cap is enforced server-side regardless; this just keeps the live
    // preview from promising more than the server will honor.
    if (raceStatus && erasing.size > 0) return;
    const hit = findSegmentAt(point);
    if (!hit) return;
    let segments = erasing.get(hit.platformId);
    if (!segments) erasing.set(hit.platformId, (segments = new Set()));
    segments.add(hit.segmentIndex);
  }

  // Flushes the accumulated drag as ONE batched `erase` message — mirroring
  // finishDrawing's "collect the whole gesture, send once on release" shape.
  // The server is the final authority and will reject anything malformed or
  // rate-limit-violating regardless of what this sends (see CLAUDE.md's
  // "shared whiteboard"/"anyone can erase anything" rationale).
  function finishErasing() {
    if (!erasing) return;
    const hits: { platformId: string; segmentIndex: number }[] = [];
    for (const [platformId, segments] of erasing) {
      for (const segmentIndex of segments) hits.push({ platformId, segmentIndex });
    }
    erasing = null;
    if (hits.length > 0) conn.send(JSON.stringify({ type: "erase", hits }));
  }

  window.addEventListener("mouseup", finishDrawing);
  window.addEventListener("mouseup", finishErasing);

  // "Ink" meter — a small live readout of how much of the room's shared
  // segment budget remains, kept current by the server's `ink` messages (see the
  // "ink" case in the message switch below, and sendInkUpdate server-side —
  // the server is the sole source of truth for the number; this just displays
  // it). Built here, alongside the rest of this connection's UI, rather than
  // up with #palette at module load — its content is meaningless before a room
  // connection exists, the same reasoning that placed the chat panel here too.
  const inkMeter = document.createElement("div");
  inkMeter.id = "ink-meter";
  inkMeter.textContent = "Ink left: …"; // placeholder text until the server's baseline reading arrives (sent right after world_state on join — see onConnect)
  document.body.appendChild(inkMeter);

  // Minigame status banner — top-center "informational chrome," following
  // #ink-meter's exact recipe (muted color, pointer-events: none, text-shadow —
  // see styles.css) but positioned to avoid colliding with #hint (top-left) or
  // #ink-meter (bottom-center). Hidden whenever there's nothing to report;
  // renderMinigameBanner is the single place that decides what it shows, kept
  // in sync purely from voteStatus/raceStatus — both populated only by the
  // minigame_* broadcast cases below, never computed locally.
  const minigameBanner = document.createElement("div");
  minigameBanner.id = "minigame-banner";
  document.body.appendChild(minigameBanner);

  // Large centered countdown — the "setup latency" beat between a yes-vote and
  // the race itself (see countdownStatus/minigame_countdown). Deliberately a
  // separate, much louder element from #minigame-banner: the user's spec asks
  // for something "large...in the middle of the screen," which a small muted
  // top-center line can't satisfy — this is the one piece of minigame chrome
  // that's meant to grab attention rather than sit quietly out of the way.
  // Hidden by default (no countdown is pending on join); updateCountdownDisplay
  // is the single place — driven every frame, since its number is wall-clock-
  // derived rather than event-derived — that decides what it shows.
  countdownEl = document.createElement("div");
  countdownEl.id = "minigame-countdown";
  countdownEl.style.display = "none";
  document.body.appendChild(countdownEl);

  function renderMinigameBanner() {
    if (raceStatus) {
      minigameBanner.textContent = "Cheese Race! First to the cheese wins";
      minigameBanner.style.display = "";
    } else if (voteStatus) {
      minigameBanner.textContent =
        `${voteStatus.proposedByUsername} wants to play Cheese Race — ${voteStatus.yesCount}/${voteStatus.totalCount} ready (type /yes or /no)`;
      minigameBanner.style.display = "";
    } else {
      minigameBanner.style.display = "none";
    }
  }
  renderMinigameBanner();

  // Erasing — right-click-drag erases every segment the cursor crosses (see
  // `erasing`/recordEraseHit/finishErasing above, wired into mousedown/
  // mousemove/mouseup alongside drawing capture). Per this game's "shared
  // whiteboard" design, anyone can erase anything (see CLAUDE.md for the
  // rationale and trade-offs); the server is the final authority and will
  // reject malformed or rate-limit-violating attempts regardless of what this
  // sends. All this listener does is preventDefault() to suppress the native
  // context menu — there's nothing on this canvas that needs it.
  canvas.addEventListener("contextmenu", (event) => {
    event.preventDefault();
  });

  // Chat — toggled open/closed with Enter (the window-level handler at the
  // bottom of this block). Collapsed, the log clips to its most recent lines —
  // a passive "ticker" so a message never goes unseen mid-game without taking
  // over the screen; expanded, it becomes a full scrollable history with an
  // input box that takes over the keyboard (see setKey's `chatOpen` guard
  // above, and the held-key release below).
  //
  // Join/leave log entries are deliberately *not* a separate protocol message —
  // they're derived right here from player_joined/player_left, the very
  // messages the roster already tracks. That keeps join/leave wording
  // consistent with however names are actually displayed (sanitized/fallback-
  // applied, never raw user input) for free, with no extra server bookkeeping
  // and no second source of truth for "who's in the room" to drift out of sync.
  let chatOpen = false;
  const MAX_CHAT_LOG = 200; // caps DOM growth over a long session — generous for "scroll back to see what you missed"

  const chatPanel = document.createElement("div");
  chatPanel.id = "chat";

  const chatLogEl = document.createElement("div");
  chatLogEl.classList.add("chat-log");
  chatPanel.appendChild(chatLogEl);

  const chatInput = document.createElement("input");
  chatInput.classList.add("chat-input");
  chatInput.placeholder = "Press Enter to chat…";
  chatInput.maxLength = MAX_CHAT_LENGTH;
  chatInput.autocomplete = "off";
  chatPanel.appendChild(chatInput);

  document.body.appendChild(chatPanel);

  function appendChatEntry(entry: ChatEntry) {
    const line = document.createElement("div");
    line.classList.add("chat-entry");
    if (entry.kind === "system") {
      line.classList.add("chat-system");
      line.textContent = entry.text;
    } else {
      const name = document.createElement("span");
      name.classList.add("chat-username");
      name.style.color = entry.color;
      name.textContent = entry.username;
      // textContent (+ a plain text node) for both pieces, never innerHTML —
      // per the GDD's "XSS via Usernames and Chat" guidance, nothing
      // user-supplied is ever parsed as markup, only ever rendered as inert text.
      line.append(name, document.createTextNode(`: ${entry.text}`));
    }
    chatLogEl.appendChild(line);
    while (chatLogEl.children.length > MAX_CHAT_LOG) chatLogEl.firstElementChild?.remove();
    chatLogEl.scrollTop = chatLogEl.scrollHeight;
  }

  function setChatOpen(open: boolean) {
    if (chatOpen === open) return;
    chatOpen = open;
    chatPanel.classList.toggle("expanded", open);
    if (open) {
      // The chat input is about to own the keyboard. Release any movement keys
      // held at this exact instant — without this, a key held down at the
      // moment chat opens would never see its matching keyup (setKey ignores
      // both while chatOpen is true), leaving the avatar "stuck" running for as
      // long as the panel stays open.
      if (keys.left || keys.right || keys.jump) {
        keys.left = keys.right = keys.jump = false;
        conn.send(JSON.stringify({ type: "input", keys }));
      }
      chatInput.focus();
      chatLogEl.scrollTop = chatLogEl.scrollHeight;
    } else {
      chatInput.value = "";
      chatInput.blur();
    }
  }

  // A small fixed vocabulary of typed commands, checked *before* anything is
  // ever treated as chat — keeping "chat is chat" and "votes/proposals are
  // typed messages" cleanly separate, mirroring how the server treats
  // isValidChat/isValidMinigamePropose/isValidMinigameVote as independent
  // shapes rather than one overloaded type with a hidden "is this secretly a
  // command" branch. Recognized commands route to their own typed,
  // server-validated messages and are *never* sent as `chat` text. An
  // unrecognized `/word` becomes a **local-only** system note — so a typo
  // doesn't land in the shared log as a literal "/rcae" for everyone to see.
  function handleSlashCommand(text: string) {
    const word = text.split(/\s+/)[0].toLowerCase();
    switch (word) {
      case "/race":
        conn.send(JSON.stringify({ type: "minigame_propose", game: "cheese_race" }));
        break;
      case "/yes":
        conn.send(JSON.stringify({ type: "minigame_vote", vote: true }));
        break;
      case "/no":
        conn.send(JSON.stringify({ type: "minigame_vote", vote: false }));
        break;
      default:
        appendChatEntry({ kind: "system", text: `Unknown command: ${word}` });
    }
  }

  chatInput.addEventListener("keydown", (event) => {
    // Stop here — the window-level handlers below (movement capture and the
    // closed-state "open chat" trigger) must never see keystrokes meant for
    // composing a message. This is also what keeps setKey's chatOpen guard from
    // ever mattering while actually typing; that guard only earns its keep if
    // focus drifts away from this input while the panel stays open (e.g. a
    // click on the canvas).
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      const text = chatInput.value.trim();
      if (text.length > 0) {
        if (text.startsWith("/")) handleSlashCommand(text);
        else conn.send(JSON.stringify({ type: "chat", text }));
      }
      setChatOpen(false);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setChatOpen(false);
    }
  });

  window.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && document.activeElement !== chatInput) {
      event.preventDefault();
      setChatOpen(!chatOpen);
    } else if (event.key === "Escape" && chatOpen) {
      setChatOpen(false);
    }
  });

  conn.addEventListener("message", (event) => {
    let data: unknown;
    try {
      data = JSON.parse(event.data);
    } catch {
      console.warn("Ignoring malformed (non-JSON) message from server:", event.data);
      return;
    }
    const msg = asRecord(data);
    if (!msg) {
      console.warn("Ignoring non-object message from server:", data);
      return;
    }

    // Every case below follows the same "right shape, or warn" structure —
    // see the isString/warnUnexpectedShape doc comment above for why this
    // uniformity (and the logging) matters more than it might look like it does.
    switch (msg.type) {
      case "snapshot": {
        if (Array.isArray(msg.players)) {
          const now = performance.now();
          const players = msg.players as PlayerSnapshot[];
          snapshotBuffer.push({ receivedAt: now, players });
          // Prune snapshots older than the current render window needs. We only ever
          // need the one snapshot just-older than the render time, plus everything
          // newer — keep buf[0] as that bracketing-older one and drop the rest.
          const cutoff = now - INTERP_DELAY_MS;
          while (snapshotBuffer.length > 2 && snapshotBuffer[1].receivedAt <= cutoff) {
            snapshotBuffer.shift();
          }

          // Reconcile local-player prediction against this fresh authority —
          // the server is still the sole source of truth (see CLAUDE.md);
          // predictLocalX only ever fills the gap *until* the next of these.
          const me = players.find((p) => p.id === conn.id);
          if (me) {
            lastServerVx = me.vx;
            if (localX === null) {
              localX = me.x; // seed on the very first snapshot — nothing to ease from yet
            } else {
              const error = me.x - localX;
              const dir = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
              // Gated by the same grace period as predictLocalX's sameDir check:
              // don't snap for a blocked read during the window where lastServerVx
              // is stale (0 from a resting state, not yet updated by the server's
              // response to a newly-pressed key).
              const inGrace = performance.now() - lastKeyChangeTime < TICK_MS * 2;
              const blocked = !inGrace && dir !== 0 && (dir > 0 ? me.vx <= 0 : me.vx >= 0);
              if (blocked || Math.abs(error) > PREDICT_SNAP_PX) localX = me.x;
              else localX += error * RECONCILE_BLEND;
            }
          }
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "world_state": {
        // Full roster/platform replacement on join — replaces any stale entries
        // from a previous connection to this room (e.g. after a reconnect). All
        // three arrays are required together: a message that's half-valid is
        // just as much "not the shape we expected" as one that's all wrong.
        if (Array.isArray(msg.players) && Array.isArray(msg.platforms) && Array.isArray(msg.activity)) {
          roster.clear();
          for (const p of msg.players as { id: string; username: string; color: string }[]) {
            roster.set(p.id, { username: p.username, color: p.color });
          }
          platforms.clear();
          for (const p of msg.platforms as Platform[]) platforms.set(p.id, p);
          // Catch-up history — replayed through the very same appendChatEntry
          // the live join/leave/chat paths use, via the same joinLeaveText
          // formatting, so a late joiner's log reads identically to having
          // been there for it (just minus the timing).
          for (const entry of msg.activity as ActivityEntry[]) {
            appendChatEntry(
              entry.kind === "chat"
                ? { kind: "chat", username: entry.username, color: entry.color, text: entry.text }
                : { kind: "system", text: joinLeaveText(entry.kind, entry.username) }
            );
          }
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "player_joined": {
        if (isString(msg.id) && isString(msg.username) && isString(msg.color)) {
          roster.set(msg.id, { username: msg.username, color: msg.color });
          // Logged for everyone the broadcast reaches, including the joiner
          // themselves — matching this project's established "no special-cased
          // local preview" pattern (see CLAUDE.md / Phases 5-6) rather than
          // reaching for a self-id check that conn.id might not even be
          // populated by yet at this exact moment in the connection lifecycle.
          appendChatEntry({ kind: "system", text: joinLeaveText("join", msg.username) });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "player_left": {
        if (isString(msg.id)) {
          // Look the username up *before* deleting — player_left only carries
          // an id (see CLAUDE.md's note on not exposing more than usernames),
          // and the roster is the only place this client knows that mapping.
          const identity = roster.get(msg.id);
          roster.delete(msg.id);
          if (identity) appendChatEntry({ kind: "system", text: joinLeaveText("leave", identity.username) });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "platform_added": {
        if (isString(msg.id) && Array.isArray(msg.points) && isString(msg.color) && isString(msg.owner)) {
          platforms.set(msg.id, { id: msg.id, points: msg.points as Point[], color: msg.color, owner: msg.owner });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "platform_removed": {
        if (isString(msg.id)) {
          platforms.delete(msg.id);
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "chat": {
        if (isString(msg.id) && isString(msg.username) && isString(msg.color) && isString(msg.text)) {
          appendChatEntry({ kind: "chat", username: msg.username, color: msg.color, text: msg.text });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "ink": {
        // The server sends `used`/`max` (not `remaining`) because it's the one
        // deriving `used` from this.platforms — `remaining = max - used` is
        // pure display-layer arithmetic on values it already gave us directly,
        // not a re-derivation of any server-side validation/business logic
        // (the kind of duplication CLAUDE.md's client-validation note warns
        // against), so computing it here is fine.
        if (isNumber(msg.used) && isNumber(msg.max)) {
          inkMeter.textContent = `Ink left: ${Math.max(0, msg.max - msg.used)} / ${msg.max}`;
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "minigame_vote_started": {
        if (
          isString(msg.game) &&
          isString(msg.proposedBy) &&
          isString(msg.proposedByUsername) &&
          Array.isArray(msg.voterIds)
        ) {
          // yesCount starts at 0 — the server's very next broadcast is always
          // minigame_vote_progress (sendVoteProgress fires right after
          // minigame_vote_started in handleMinigamePropose, both over one
          // ordered connection), which corrects this to reflect the proposer's
          // auto-yes essentially instantly. Deriving "1" here from proposedBy
          // would just be re-deriving server business logic a frame early.
          voteStatus = { proposedByUsername: msg.proposedByUsername, yesCount: 0, totalCount: msg.voterIds.length };
          renderMinigameBanner();
          appendChatEntry({ kind: "system", text: `${msg.proposedByUsername} wants to play Cheese Race — type /yes or /no` });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "minigame_vote_progress": {
        if (isNumber(msg.yesCount) && isNumber(msg.totalCount)) {
          if (voteStatus) voteStatus = { ...voteStatus, yesCount: msg.yesCount, totalCount: msg.totalCount };
          renderMinigameBanner();
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "minigame_vote_resolved": {
        const outcome = msg.outcome;
        if (isString(msg.game) && (outcome === "started" || outcome === "cancelled")) {
          voteStatus = null;
          renderMinigameBanner();
          if (outcome === "cancelled") {
            const reason = msg.reason;
            const text =
              reason === "no_vote" && isString(msg.byUsername) ? `${msg.byUsername} doesn't want to race` :
              reason === "timeout" ? "The vote to race timed out" :
              reason === "left" ? "The vote to race was cancelled — not enough players" :
              "The vote to race was cancelled";
            appendChatEntry({ kind: "system", text });
          }
          // outcome === "started" needs no chat note of its own — minigame_countdown
          // (broadcast immediately after, by the server's design — see CLAUDE.md/
          // the plan's "kept distinct because... could diverge for a future
          // minigame with setup latency," now realized exactly that way: the
          // countdown *is* that setup latency) announces what's coming.
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "minigame_countdown": {
        if (isString(msg.game) && isNumber(msg.endsAt)) {
          countdownStatus = { endsAt: msg.endsAt };
          // Derived from the server's own endsAt rather than a client-side copy
          // of MINIGAME_COUNTDOWN_MS — exactly the "trust the shape, not the
          // fields, and don't re-derive server business logic" posture CLAUDE.md
          // pins: the number this prints can never drift from what the server
          // actually scheduled, even if that constant changes on the server side.
          const seconds = Math.max(0, Math.round((msg.endsAt - Date.now()) / 1000));
          appendChatEntry({ kind: "system", text: `Cheese Race starting in ${seconds} — get ready!` });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "minigame_started": {
        const cheese = asRecord(msg.cheese);
        if (isString(msg.game) && cheese && isNumber(cheese.x) && isNumber(cheese.y)) {
          countdownStatus = null;
          raceStatus = { cheese: { x: cheese.x, y: cheese.y } };
          renderMinigameBanner();
          appendChatEntry({ kind: "system", text: "Cheese Race! First to the cheese wins" });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      case "minigame_ended": {
        if (isString(msg.winnerId) && isString(msg.winnerUsername)) {
          raceStatus = null;
          renderMinigameBanner();
          appendChatEntry({ kind: "system", text: `${msg.winnerUsername} won the Cheese Race!` });
        } else {
          warnUnexpectedShape(msg.type, msg);
        }
        break;
      }
      default:
        console.warn("Ignoring message from server with unrecognized type:", msg.type);
    }
  });

  // Reset right before the loop actually starts — frame()'s first dtMs is
  // measured from here, not from module load (which could be an arbitrarily
  // long join-screen pause earlier) or predictLocalX would integrate one
  // enormous fictitious delta on its very first call.
  lastFrameTime = performance.now();
  requestAnimationFrame(frame);
}

function drawGround() {
  ctx.fillStyle = "#3a3f4b";
  ctx.fillRect(0, WORLD_HEIGHT - GROUND_THICKNESS, WORLD_WIDTH, GROUND_THICKNESS);
}

// Strokes a point array as a thick rounded path — purely cosmetic, mirroring
// the prototype's drawPlatform. Physics fidelity comes from the server's
// deterministically-generated bodies (see CLAUDE.md's rendering convention);
// this just needs to *look* like the same shape.
function drawPlatformPath(points: Point[], color: string) {
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = color;
  ctx.lineWidth = PLATFORM_THICKNESS;
  ctx.stroke();
}

function drawPlatforms() {
  for (const platform of platforms.values()) {
    drawPlatformPath(platform.points, platform.color);
  }
}

// Live preview of the in-progress stroke, in the locally-selected color —
// the server has the final say (this redraws from `platforms` once it
// broadcasts `platform_added`, including back to the drawer).
function drawInProgressPath() {
  if (drawing) drawPlatformPath(drawing, selectedColor);
}

// Live preview of an in-progress erase drag — re-strokes each segment
// `erasing` has accumulated so far (see its declaration) in a wider, danger-
// colored line *underneath* the platform's own stroke (drawn after this, in
// `frame`), producing a glow around exactly what mouse-up will remove. Purely
// cosmetic and ephemeral: nothing is actually gone until the server broadcasts
// `platform_removed`/`platform_added`, mirroring `drawInProgressPath`'s "local
// preview, server has final say" posture for drawing.
function drawErasePreview() {
  if (!erasing) return;
  for (const [platformId, segments] of erasing) {
    const platform = platforms.get(platformId);
    if (!platform) continue;
    for (const index of segments) {
      const start = platform.points[index];
      const end = platform.points[index + 1];
      if (!start || !end) continue;
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.lineCap = "round";
      ctx.strokeStyle = ERASE_PREVIEW_COLOR;
      ctx.lineWidth = PLATFORM_THICKNESS + 6;
      ctx.stroke();
    }
  }
}

// Cheese render — mirrors drawPlatforms/drawPlayers's shape: a filled circle
// in PALETTE_COLORS[2] ("#ffd479" — already the palette's "cheese yellow," no
// new color to invent), dark outline matching the avatar stroke style. The
// cheese is a server-authoritative *target point*, never a physics body —
// raceStatus is populated only by minigame_started and cleared only by
// minigame_ended, so this purely renders what the server already decided.
function drawCheese() {
  if (!raceStatus) return;
  const { cheese } = raceStatus;
  ctx.beginPath();
  ctx.arc(cheese.x, cheese.y, CHEESE_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = PALETTE_COLORS[2];
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = "#1b1d23";
  ctx.stroke();
}

// The countdown's number is wall-clock-derived (ticks every frame from
// countdownStatus.endsAt and Date.now()), not event-derived like every other
// piece of minigame chrome — so unlike renderMinigameBanner (called only when
// voteStatus/raceStatus actually change), this runs from frame() every frame,
// the one place that already recomputes everything from "now." Recomputing
// from the absolute endsAt each time (rather than counting down a local
// duration) is what keeps it immune to drift or a missed frame.
function updateCountdownDisplay() {
  if (!countdownStatus) {
    countdownEl.style.display = "none";
    return;
  }
  const secondsLeft = Math.max(0, Math.ceil((countdownStatus.endsAt - Date.now()) / 1000));
  countdownEl.textContent = String(secondsLeft);
  countdownEl.style.display = "";
}

// Returns each player's position interpolated to `renderTime` (which is
// deliberately in the past — see INTERP_DELAY_MS), blending the two snapshots
// that bracket it. Purely a rendering transform over server-authoritative
// positions: no physics, no prediction, the server is still the only thing that
// decides where anyone actually is.
function playersAt(renderTime: number): PlayerSnapshot[] {
  const buf = snapshotBuffer;
  if (buf.length === 0) return [];

  // Find the newest snapshot at or before renderTime ("older"), and the one
  // right after it ("newer").
  let older: Snapshot | null = null;
  let newer: Snapshot | null = null;
  for (let i = buf.length - 1; i >= 0; i--) {
    if (buf[i].receivedAt <= renderTime) {
      older = buf[i];
      newer = buf[i + 1] ?? null;
      break;
    }
  }

  // renderTime is before everything we have (just connected): show the oldest.
  if (!older) return buf[0].players;
  // renderTime is past the newest (server stalled / packets stopped): hold the
  // last known positions. Deliberately NOT extrapolated — holding is safe and
  // keeps this strictly "render server data," matching the no-client-physics rule.
  if (!newer) return older.players;

  const span = newer.receivedAt - older.receivedAt;
  const t = span > 0 ? Math.max(0, Math.min(1, (renderTime - older.receivedAt) / span)) : 0;

  const newerById = new Map(newer.players.map((p) => [p.id, p]));
  const result: PlayerSnapshot[] = [];
  for (const o of older.players) {
    const n = newerById.get(o.id);
    if (n) {
      // Present in both: linear-interpolate position. vx/vy aren't used by
      // drawPlayers, so carry the newer values through unchanged for shape parity.
      result.push({
        id: o.id,
        x: o.x + (n.x - o.x) * t,
        y: o.y + (n.y - o.y) * t,
        vx: n.vx,
        vy: n.vy,
      });
      newerById.delete(o.id);
    } else {
      // Only in the older snapshot (player left between the two) — draw last
      // known spot; it disappears once `older` advances past it.
      result.push(o);
    }
  }
  // Anything left in newerById only appeared in the newer snapshot (just joined).
  for (const n of newerById.values()) result.push(n);

  return result;
}

function drawPlayers(players: PlayerSnapshot[]) {
  for (const player of players) {
    const isMe = player.id === conn.id;
    const identity = roster.get(player.id);

    ctx.beginPath();
    ctx.arc(player.x, player.y, PLAYER_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = identity?.color ?? DEFAULT_AVATAR_COLOR;
    ctx.fill();
    ctx.lineWidth = isMe ? 3 : 2;
    ctx.strokeStyle = isMe ? "#6ee7b7" : "#1b1d23";
    ctx.stroke();

    if (identity) {
      ctx.fillStyle = "#c8ccd6";
      ctx.font = "12px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(isMe ? `${identity.username} (you)` : identity.username, player.x, player.y - PLAYER_RADIUS - 6);
    }
  }
}

function frame() {
  const now = performance.now();
  const dtMs = now - lastFrameTime;
  lastFrameTime = now;

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  drawGround();
  drawErasePreview();
  drawPlatforms();
  drawCheese();

  predictLocalX(dtMs);
  // Remote players render exactly as before — pure interpolation of server
  // data. Only the local entry gets its x replaced with the predicted value
  // (y stays interpolated; see predictLocalX's "honest limitation" note).
  // Build a fresh array rather than mutating playersAt's result in place: its
  // "only in older"/"only in newer" branches return snapshot objects *by
  // reference* from snapshotBuffer, and overwriting .x on one of those would
  // permanently corrupt that buffered snapshot for every future frame's
  // interpolation and reconciliation.
  const players = playersAt(now - INTERP_DELAY_MS).map((player) =>
    localX !== null && player.id === conn.id ? { ...player, x: localX } : player
  );
  drawPlayers(players);

  drawInProgressPath();
  updateCountdownDisplay();
  requestAnimationFrame(frame);
}

// Lobby — collects identity (name + avatar color) and a room before connecting.
// Per the GDD's "Room & Lobby System": room IDs are short, shareable codes
// generated client-side and carried in the URL (?room=XYZABC) so a player can
// hand the link to a friend and land in the same room.
const MAX_USERNAME_LENGTH = 20; // mirrors the server's MAX_USERNAME_LENGTH
const ROOM_CODE_LENGTH = 6;
const ROOM_CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no ambiguous 0/O/1/I/L
const ROOM_CODE_PATTERN = /^[A-Z0-9]{1,12}$/;

type DirectoryRoom = { roomId: string; playerCount: number };

// Builds the URL to the directory party's singleton room (see directory.ts) —
// the join screen's "active games" list is a plain cross-origin fetch, not a
// PartySocket connection, so it needs a full URL rather than just a host.
function directoryUrl(): string {
  // Picks http/https the same way PartySocket picks ws/wss for PARTYKIT_HOST —
  // a narrower check than partysocket's full private-IP-range sniffing suffices
  // here, since this project's dev server only ever runs on 127.0.0.1 (see
  // CLAUDE.md/TODO.md), never some other LAN address.
  const protocol = PARTYKIT_HOST.startsWith("localhost:") || PARTYKIT_HOST.startsWith("127.0.0.1:") ? "http" : "https";
  // "main" must match directory.ts's exported ROOM_ID — duplicated here because
  // this is a separate (browser) bundle that can't import a backend module;
  // same cross-boundary-constant pattern as DEFAULT_AVATAR_COLOR/MAX_CHAT_LENGTH.
  return `${protocol}://${PARTYKIT_HOST}/parties/directory/main`;
}

// Best-effort: an unreachable directory (cold start, network hiccup, or simply
// no rooms having pinged it yet) just yields an empty list — never a blocker to
// creating or joining a room directly by code, which has always worked without it.
async function fetchActiveRooms(): Promise<DirectoryRoom[]> {
  try {
    // The directory's player counts change by the second (rooms fill, empty,
    // age out), so a browser-cached response — observed in testing to persist
    // across reloads of this exact URL — would show stale numbers or hide
    // freshly-emptied rooms indefinitely. This list is the one place staleness
    // would visibly mislead a player about where to join.
    const res = await fetch(directoryUrl(), { cache: "no-store" });
    const data = asRecord(await res.json());
    if (!data || !Array.isArray(data.rooms)) return [];
    const rooms: DirectoryRoom[] = [];
    for (const entry of data.rooms) {
      const room = asRecord(entry);
      if (room && isString(room.roomId) && isNumber(room.playerCount)) {
        rooms.push({ roomId: room.roomId, playerCount: room.playerCount });
      }
    }
    return rooms;
  } catch {
    return [];
  }
}

// Replaces roomListEl's contents with either an empty-state note or one
// clickable entry per active room — picking an entry hands its code to onPick
// (which the join screen uses to fill the room-code field) rather than
// reaching into the form directly, keeping this a dumb render function.
function renderRoomList(roomListEl: HTMLElement, rooms: DirectoryRoom[], onPick: (roomId: string) => void) {
  roomListEl.replaceChildren();
  if (rooms.length === 0) {
    const empty = document.createElement("p");
    empty.classList.add("room-list-empty");
    empty.textContent = "No active games right now — start your own below.";
    roomListEl.appendChild(empty);
    return;
  }
  for (const { roomId, playerCount } of rooms) {
    const entry = document.createElement("button");
    entry.type = "button";
    entry.classList.add("room-list-entry");

    const code = document.createElement("span");
    code.textContent = roomId;
    const count = document.createElement("span");
    count.classList.add("room-list-count");
    count.textContent = `${playerCount} ${playerCount === 1 ? "player" : "players"}`;
    entry.append(code, count);

    entry.addEventListener("click", () => onPick(roomId));
    roomListEl.appendChild(entry);
  }
}

function generateRoomCode(): string {
  let code = "";
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    code += ROOM_CODE_CHARS[Math.floor(Math.random() * ROOM_CODE_CHARS.length)];
  }
  return code;
}

function roomCodeFromUrl(): string | null {
  const raw = new URLSearchParams(location.search).get("room");
  if (!raw) return null;
  const normalized = raw.trim().toUpperCase();
  return ROOM_CODE_PATTERN.test(normalized) ? normalized : null;
}

let selectedAvatarColor = PALETTE_COLORS[3]; // a green, distinct from the platform palette's default red (PALETTE_COLORS[0])

function buildJoinScreen() {
  const overlay = document.createElement("div");
  overlay.id = "join-screen";

  const card = document.createElement("form");
  card.id = "join-card";

  const title = document.createElement("h1");
  title.textContent = "Mouse";
  card.appendChild(title);

  const tagline = document.createElement("p");
  tagline.textContent = "A whiteboard where everyone's mouse can run and jump on whatever gets drawn.";
  card.appendChild(tagline);

  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Name";
  // No id: the <label> wraps this input directly (an implicit label
  // association — no `for`/`id` pairing needed), and nothing else looks it up.
  const nameInput = document.createElement("input");
  nameInput.maxLength = MAX_USERNAME_LENGTH;
  nameInput.placeholder = "Cheddar";
  nameInput.autocomplete = "off";
  nameLabel.appendChild(nameInput);
  card.appendChild(nameLabel);

  const colorLabel = document.createElement("div");
  colorLabel.classList.add("join-field-label");
  colorLabel.textContent = "Color";
  card.appendChild(colorLabel);

  // No id here: nothing looks this element up by id, and ".swatch-row" alone
  // already carries all of its styling (see CLAUDE.md's "id vs. class" — an id
  // with neither a CSS rule nor a lookup is just an unused name to track).
  const colorPicker = document.createElement("div");
  colorPicker.classList.add("swatch-row");
  for (const color of PALETTE_COLORS) {
    const swatch = document.createElement("button");
    swatch.type = "button";
    swatch.classList.add("swatch");
    swatch.style.backgroundColor = color;
    swatch.setAttribute("aria-label", `Use avatar color ${color}`);
    swatch.classList.toggle("selected", color === selectedAvatarColor);
    swatch.addEventListener("click", () => {
      selectedAvatarColor = color;
      for (const el of colorPicker.querySelectorAll(".swatch")) el.classList.remove("selected");
      swatch.classList.add("selected");
    });
    colorPicker.appendChild(swatch);
  }
  card.appendChild(colorPicker);

  // Active-games list — sits above the room-code field so picking one is the
  // natural first move, while typing/keeping a code remains just as available.
  // Populated asynchronously below (after roomInput exists, since picking an
  // entry fills it in); starts in a "loading" state rather than empty so a
  // slow directory fetch doesn't briefly look identical to "no rooms active".
  const roomListLabel = document.createElement("div");
  roomListLabel.classList.add("join-field-label");
  roomListLabel.textContent = "Active games";
  card.appendChild(roomListLabel);

  const roomList = document.createElement("div");
  roomList.classList.add("room-list");
  const loading = document.createElement("p");
  loading.classList.add("room-list-empty");
  loading.textContent = "Loading active games…";
  roomList.appendChild(loading);
  card.appendChild(roomList);

  // Debug-only toggle: empty rooms are filtered out by default — joining one
  // is meaningless (connecting just spins up a fresh room either way), so
  // they'd only ever clutter the list — but seeing them is occasionally handy
  // for watching room lifecycle/churn during development, hence "(debug)".
  // Wiring (the `change` listener and the fetch that populates `activeRooms`)
  // lives further below, alongside `refreshRoomList` — both need `roomInput`,
  // declared next.
  const emptyToggleLabel = document.createElement("label");
  emptyToggleLabel.classList.add("room-list-toggle");
  const emptyToggle = document.createElement("input");
  emptyToggle.type = "checkbox";
  emptyToggleLabel.append(emptyToggle, document.createTextNode(" Show empty rooms (debug)"));
  card.appendChild(emptyToggleLabel);

  const roomLabel = document.createElement("label");
  roomLabel.textContent = "Room code";
  // Same reasoning as nameInput above — implicitly labeled, never looked up.
  const roomInput = document.createElement("input");
  roomInput.maxLength = 12;
  roomInput.autocomplete = "off";
  roomInput.value = roomCodeFromUrl() ?? generateRoomCode();
  roomLabel.appendChild(roomInput);
  card.appendChild(roomLabel);

  // Picking a room fills its code into the field and focuses it (rather than
  // submitting immediately) — keeps "join a listed room" and "type/paste a
  // code" the same one-button flow, and lets the player glance at/edit the
  // code (or change their name/color) before committing.
  //
  // `activeRooms` holds the full fetched list (including empty rooms) so
  // toggling the debug checkbox is a pure re-filter — no need to refetch.
  let activeRooms: DirectoryRoom[] = [];
  function refreshRoomList() {
    const visible = emptyToggle.checked ? activeRooms : activeRooms.filter((room) => room.playerCount > 0);
    renderRoomList(roomList, visible, (roomId) => {
      roomInput.value = roomId;
      roomInput.focus();
    });
  }
  emptyToggle.addEventListener("change", refreshRoomList);

  fetchActiveRooms().then((rooms) => {
    activeRooms = rooms;
    refreshRoomList();
  });

  const hint = document.createElement("p");
  hint.classList.add("join-hint");
  hint.textContent = "Share the room code (or this page's URL) with friends to play together.";
  card.appendChild(hint);

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.id = "join-submit";
  submit.textContent = "Play";
  card.appendChild(submit);

  card.addEventListener("submit", (event) => {
    event.preventDefault();

    const username = nameInput.value.trim().slice(0, MAX_USERNAME_LENGTH);
    const typedRoom = roomInput.value.trim().toUpperCase().slice(0, 12);
    const roomCode = ROOM_CODE_PATTERN.test(typedRoom) ? typedRoom : generateRoomCode();

    const url = new URL(location.href);
    url.searchParams.set("room", roomCode);
    history.replaceState(null, "", url);

    overlay.remove();
    startGame(roomCode, { username, color: selectedAvatarColor });
  });

  overlay.appendChild(card);
  document.body.appendChild(overlay);
  nameInput.focus();
}

buildJoinScreen();
